import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAreasNav } from '../../contexts/AreasContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cameraHasCatalogModel, useModelsCatalog } from '../../contexts/ModelsContext';
import {
  IconAreas,
  IconCamera,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconDashboard,
  IconLogout,
  IconMoon,
  IconPlus,
  IconShield,
  IconSun,
  IconUser,
  IconWallet,
} from '../icons/Icons';
import type { IconProps } from '../icons/Icons';
import ModelIcon from '../ModelIcon/ModelIcon';
import './Sidebar.css';

interface SidebarProps {
  /** Открыто ли меню на мобильных (drawer). */
  mobileOpen: boolean;
  onCloseMobile: () => void;
  /** Свёрнуто ли меню на десктопе (только иконки). */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

interface NavLinkItem {
  id: string;
  title: string;
  url: string;
  Icon: React.FC<IconProps>;
}

const AREAS_GROUP_KEY = 'flypix:sidebar-areas-open';

const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen,
  onCloseMobile,
  collapsed = false,
  onToggleCollapsed,
}) => {
  const { user, isAdmin, logout } = useAuth();
  const { areas } = useAreasNav();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { categories, models, cameras } = useModelsCatalog();
  const navigate = useNavigate();
  const location = useLocation();

  const [areasOpen, setAreasOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AREAS_GROUP_KEY) !== 'closed';
    } catch {
      return true;
    }
  });

  // Открываем группу участков, если пользователь находится внутри участка
  useEffect(() => {
    if (location.pathname.startsWith('/area/')) {
      setAreasOpen(true);
    }
  }, [location.pathname]);

  const toggleAreas = () => {
    if (collapsed) {
      onToggleCollapsed?.();
      setAreasOpen(true);
      try {
        localStorage.setItem(AREAS_GROUP_KEY, 'open');
      } catch {
        /* ignore */
      }
      return;
    }

    setAreasOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(AREAS_GROUP_KEY, next ? 'open' : 'closed');
      } catch {
        /* сохранение состояния не критично */
      }
      return next;
    });
  };

  // Разделы, которые идут после группы «Участки»
  const secondaryLinks = useMemo<NavLinkItem[]>(
    () => [
      ...(!user?.is_subaccount ? [{ id: 'balance', title: 'Баланс', url: '/balance', Icon: IconWallet }] : []),
      ...(!user?.is_subaccount ? [{ id: 'subaccounts', title: 'Субаккаунты', url: '/subaccounts', Icon: IconUser }] : []),
      { id: 'profile', title: 'Профиль', url: '/profile', Icon: IconUser },
    ],
    [user?.is_subaccount]
  );

  const handleLogout = async () => {
    onCloseMobile();
    await logout();
    navigate('/login');
  };

  const isAreaActive = location.pathname.startsWith('/area/');
  const modelCategoryLinks = useMemo(() => {
    return categories.filter((category) => {
      const names = new Set(models.filter((model) => model.category === category.id).map((model) => model.name.toLowerCase()));
      return cameras.some((camera) => cameraHasCatalogModel(camera.models, names));
    });
  }, [categories, models, cameras]);
  const showAreasSub = areasOpen && !collapsed;

  return (
    <>
      <button
        type="button"
        className={`sidebar-scrim${mobileOpen ? ' sidebar-scrim--visible' : ''}`}
        aria-hidden={!mobileOpen}
        tabIndex={-1}
        onClick={onCloseMobile}
      />

      <aside
        className={`sidebar${mobileOpen ? ' sidebar--open' : ''}${
          collapsed ? ' sidebar--collapsed' : ''
        }`}
        aria-label="Основная навигация"
        data-collapsed={collapsed ? 'true' : 'false'}
      >
        <div className="sidebar__shell" aria-hidden="true" />

        <div className="sidebar__top">
          <NavLink
            to="/dashboard"
            className="sidebar__logo"
            onClick={onCloseMobile}
            title="Flypix"
          >
            <img
              src={`${process.env.PUBLIC_URL}/branding/full-logo-light.svg`}
              alt="Flypix"
              className="sidebar__logo-img sidebar__logo-img--light sidebar__logo-img--full"
            />
            <img
              src={`${process.env.PUBLIC_URL}/branding/full-logo.svg`}
              alt="Flypix"
              className="sidebar__logo-img sidebar__logo-img--dark sidebar__logo-img--full"
            />
            <img
              src={`${process.env.PUBLIC_URL}/branding/icon-logo.svg`}
              alt="Flypix"
              className="sidebar__logo-img sidebar__logo-img--icon"
            />
          </NavLink>

          <button
            type="button"
            className="sidebar__close"
            aria-label="Закрыть меню"
            onClick={onCloseMobile}
          >
            <IconClose size={18} />
          </button>
        </div>

        <nav className="sidebar__nav ui-scroll" aria-label="Разделы">
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `sidebar__item${isActive ? ' sidebar__item--active' : ''}`
            }
            onClick={onCloseMobile}
            title="Дашборд"
          >
            <IconDashboard size={20} />
            <span className="sidebar__item-label">Дашборд</span>
          </NavLink>

          <div className={`sidebar__group${showAreasSub ? ' sidebar__group--open' : ''}`}>
            <button
              type="button"
              className={`sidebar__item sidebar__item--group${
                isAreaActive && !showAreasSub ? ' sidebar__item--active' : ''
              }`}
              aria-expanded={showAreasSub}
              onClick={toggleAreas}
              title="Участки"
            >
              <IconAreas size={20} />
              <span className="sidebar__item-label">Участки</span>
              {areas.length > 0 && (
                <span className="sidebar__count">{areas.length}</span>
              )}
              <IconChevronDown size={16} className="sidebar__chevron" />
            </button>

            {showAreasSub && (
              <div className="sidebar__sub">
                {areas.length === 0 ? (
                  !user?.is_subaccount && <NavLink
                    to="/dashboard"
                    className="sidebar__subitem sidebar__subitem--muted"
                    onClick={onCloseMobile}
                  >
                    <IconPlus size={16} />
                    <span className="sidebar__item-label">Создать участок</span>
                  </NavLink>
                ) : (
                  areas.map((area) => (
                    <NavLink
                      key={area.id}
                      to={`/area/${area.id}`}
                      className={({ isActive }) =>
                        `sidebar__subitem${isActive ? ' sidebar__subitem--active' : ''}`
                      }
                      onClick={onCloseMobile}
                      title={area.name}
                    >
                      <IconCamera size={16} />
                      <span className="sidebar__item-label">{area.name}</span>
                      {typeof area.cameras_count === 'number' && (
                        <span className="sidebar__count">{area.cameras_count}</span>
                      )}
                    </NavLink>
                  ))
                )}
              </div>
            )}
          </div>

          {modelCategoryLinks.map((category) => (
            <NavLink
              key={category.id}
              to={`/model-category/${encodeURIComponent(category.id)}`}
              className={({ isActive }) =>
                `sidebar__item${isActive ? ' sidebar__item--active' : ''}`
              }
              onClick={onCloseMobile}
              title={category.title}
            >
              <ModelIcon categoryId={category.id} models={models} size={20} />
              <span className="sidebar__item-label">{category.title}</span>
            </NavLink>
          ))}

          {secondaryLinks.map(({ id, title, url, Icon }) => (
            <NavLink
              key={id}
              to={url}
              className={({ isActive }) =>
                `sidebar__item${isActive ? ' sidebar__item--active' : ''}`
              }
              onClick={onCloseMobile}
              title={title}
            >
              <Icon size={20} />
              <span className="sidebar__item-label">{title}</span>
            </NavLink>
          ))}

          {isAdmin && (
            <>
              <div className="sidebar__divider" role="separator" />
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `sidebar__item${isActive ? ' sidebar__item--active' : ''}`
                }
                onClick={onCloseMobile}
                title="Админ-панель"
              >
                <IconShield size={20} />
                <span className="sidebar__item-label">Админ-панель</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="sidebar__footer">
          {onToggleCollapsed && (
            <button
              type="button"
              className="sidebar__collapse"
              onClick={onToggleCollapsed}
              title={collapsed ? 'Развернуть' : 'Свернуть'}
              aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            >
              {collapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
            </button>
          )}

          <button
            type="button"
            className="sidebar__ghost-btn"
            onClick={toggleTheme}
            title={resolvedTheme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            aria-label={
              resolvedTheme === 'dark' ? 'Включить светлую тему' : 'Включить тёмную тему'
            }
          >
            {resolvedTheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
            <span className="sidebar__item-label">
              {resolvedTheme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            </span>
          </button>

          <div className="sidebar__user">
            <span className="sidebar__avatar" aria-hidden="true">
              {(user?.name || 'F').trim().charAt(0).toUpperCase()}
            </span>
            <span className="sidebar__user-text">
              <span className="sidebar__user-name">{user?.name || 'Пользователь'}</span>
              <span className="sidebar__user-mail">{user?.email}</span>
            </span>
            <button
              type="button"
              className="sidebar__logout"
              aria-label="Выйти из кабинета"
              title="Выйти"
              onClick={() => void handleLogout()}
            >
              <IconLogout size={18} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
